import { createQueryKey } from '@lifeomic/one-query/util';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { CompositeReportDefinitionSummary } from '../../../AITemplates/models';
import { ModelUtils } from '../../../AITemplates/models';
import {
  REST_API_HOOKS_NAME,
  useApiClient,
} from '../../../../hooks/useRestAPIHooks';

/**
 * Loads each composite report definition by durable id so we can tell whether it
 * has template sections (list summaries from GET /v1/ai-templates do not include sections).
 */
export function useCrdTemplateSectionAvailability(
  compositeReportDefinitions: CompositeReportDefinitionSummary[],
) {
  const apiClient = useApiClient();

  const durableIds = useMemo(
    () => compositeReportDefinitions.map(c => c.durableId),
    [compositeReportDefinitions],
  );

  const queries = useMemo(
    () =>
      durableIds.map(durableId => ({
        queryKey: [
          createQueryKey(
            REST_API_HOOKS_NAME,
            'GET /v1/ai-templates/durable/:durableId',
            { durableId },
          ),
        ],
        queryFn: async () => {
          try {
            const res = await apiClient.request(
              'GET /v1/ai-templates/durable/:durableId',
              { durableId },
            );
            const item = ModelUtils.templatingItemDetailFromRawData(
              res.data.template,
            );
            if (item.type !== 'compositeReportDefinition') {
              return 0;
            }
            return item.sections.length;
          } catch {
            return 0;
          }
        },
        enabled: durableIds.length > 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
      })),
    [apiClient, durableIds],
  );

  const results = useQueries({ queries });

  const resultsMatchList = results.length === durableIds.length;

  const isLoading =
    durableIds.length > 0 &&
    (!resultsMatchList || results.some(result => result.status === 'loading'));

  const hasTemplateSections = useCallback(
    (durableId: string): boolean | undefined => {
      if (!resultsMatchList) {
        return undefined;
      }
      const index = durableIds.indexOf(durableId);
      if (index < 0) {
        return undefined;
      }
      const result = results[index];
      if (result.status === 'loading') {
        return undefined;
      }
      if (result.status === 'error') {
        return false;
      }
      return (result.data ?? 0) > 0;
    },
    [durableIds, results, resultsMatchList],
  );

  return { isLoading, hasTemplateSections };
}
